import {
  ArcVoiceDevice,
  IArcVoiceAnswer,
  IArcVoiceCallCheckpoint,
  IArcVoiceCallLine,
  IArcVoiceSession,
} from '@common/interfaces/arcVoice'
import { AtlasAIMessageStatus } from '@common/interfaces/atlasai'
import {
  AimNoteScopeType,
  AimPinTargetType,
  ChannelNotificationLevel,
  ChannelVisibility,
  IChannelAttachmentInput,
  IChannelMessage,
  SearchOrder,
} from '@common/interfaces/channels'
import {
  IHuddle,
  IHuddleAudioChunk,
  IHuddleHistoryItem,
  IHuddleJoin,
  IHuddleTranscript,
} from '@common/interfaces/huddles'
import { ThreadsFilter } from '@common/interfaces/threadsFilter'
import { RawDraftContentState } from 'draft-js'
import Base from './base'

/** The Search surface's people and date filters, shared by both of its requests. */
export interface ISearchFilters {
  memberIds?: string[]
  /** `YYYY-MM-DD`, inclusive. */
  dateFrom?: string
  dateTo?: string
}

export interface ISearchMessagesParams extends ISearchFilters {
  query: string
  channelId?: string
  orderBy?: SearchOrder
  limit?: number
  offset?: number
}

export interface ISearchConversationsParams extends ISearchFilters {
  query?: string
}

export interface ISendChannelMessageParams {
  contentState?: RawDraftContentState
  content?: string
  /** The message this one answers; the server checks it is in the same channel. */
  replyToMessageId?: string
  /**
   * The portal page the sender is looking at, forwarded to Arc as ambient
   * context. Filled in by the `sendMessage` action rather than by callers — see
   * the note there.
   */
  pageUrl?: string
  /** Minted once per send and repeated on every retry, so the server stores it once. */
  clientMessageId?: string
  attachments?: IChannelAttachmentInput[]
}

/** A message to post later: the same body Send takes, and when. */
export interface IScheduleChannelMessageParams extends Omit<
  ISendChannelMessageParams,
  'pageUrl' | 'clientMessageId'
> {
  /** ISO 8601 with the sender's offset. */
  sendAt: string
}

/** The first message of a conversation that is created only when it sends. */
export interface IScheduleFirstMessageParams extends Omit<
  IScheduleChannelMessageParams,
  'replyToMessageId'
> {
  userIds: string[]
  arcModel?: string
}

export type IUpdateScheduledChannelMessageParams = Partial<
  Pick<IScheduleChannelMessageParams, 'sendAt' | 'content' | 'contentState'>
>

export interface ISendVoiceMemoParams {
  file: File
  durationMs: number
  replyToMessageId?: string
  /** What was typed beside the memo, @-mentions and all. Absent for a memo on its own. */
  contentState?: RawDraftContentState
  /** Stamped by the `sendVoiceMemo` action, as `sendMessage` does. */
  pageUrl?: string
}

/** How each turn a Stop named ended up: CANCELLED when it stopped it, otherwise how it had finished. */
export interface ICancelArcRunResult {
  turns: Array<{ id: string; status: AtlasAIMessageStatus }>
}

export default class ChannelsApi extends Base {
  // --- Channels ---

  list(params: { includeArchived?: boolean } = {}) {
    return this.apiClient.get('channels', params)
  }

  browse(
    params: { search?: string; includeJoined?: boolean; page?: number; perPage?: number } = {},
  ) {
    return this.apiClient.get('channels/browse', params)
  }

  get(channelId: string) {
    return this.apiClient.get(`channels/${channelId}`)
  }

  create(data: {
    name: string
    topic?: string
    description?: string
    visibility?: ChannelVisibility
    memberUserIds?: string[]
  }) {
    return this.apiClient.post('channels', data)
  }

  update(
    channelId: string,
    data: {
      name?: string
      emoji?: string
      topic?: string
      description?: string
      visibility?: ChannelVisibility
      arcAutoReply?: boolean
    },
  ) {
    return this.apiClient.put(`channels/${channelId}`, data)
  }

  archive(channelId: string, isArchived: boolean) {
    return this.apiClient.post(`channels/${channelId}/archive`, { isArchived })
  }

  findDm(userIds: string[]) {
    return this.apiClient.get('channels/dm', { userIds })
  }

  /**
   * `arcModel` is applied when the conversation is created rather than set on it
   * afterwards: the draft panel sends its first message the moment this resolves,
   * so a follow-up SetArcModel would race the run it was meant to configure.
   */
  openDm(userIds: string[], arcModel?: string) {
    return this.apiClient.post('channels/dm', { userIds, ...(arcModel ? { arcModel } : {}) })
  }

  // --- Membership ---

  join(channelId: string) {
    return this.apiClient.post(`channels/${channelId}/join`)
  }

  leave(channelId: string) {
    return this.apiClient.post(`channels/${channelId}/leave`)
  }

  listMembers(channelId: string) {
    return this.apiClient.get(`channels/${channelId}/members`)
  }

  addMembers(channelId: string, userIds: string[]) {
    return this.apiClient.post(`channels/${channelId}/members`, { userIds })
  }

  removeMember(channelId: string, userId: string) {
    return this.apiClient.delete(`channels/${channelId}/members/${userId}`)
  }

  createShareLink(channelId: string) {
    return this.apiClient.post(`channels/${channelId}/share-links`)
  }

  // Keyed by the token alone — whoever holds a link doesn't know the channel id,
  // and deriving it server-side is what stops one being paired with another.
  resolveShareLink(shareId: string) {
    return this.apiClient.get(`channels/shares/${shareId}`)
  }

  joinShare(shareId: string) {
    return this.apiClient.post(`channels/shares/${shareId}/join`)
  }

  updateSettings(
    channelId: string,
    data: {
      isPinned?: boolean
      notificationLevel?: ChannelNotificationLevel
      mutedUntil?: string | null
      overridesGroupMute?: boolean
    },
  ) {
    return this.apiClient.put(`channels/${channelId}/settings`, data)
  }

  // --- Messages ---

  /**
   * `before`/`beforeId` identify the oldest message already held — a timestamp
   * alone can't separate messages written in the same transaction, which share
   * one exactly. Cursor rather than offset so paging upward is stable while new
   * messages arrive at the bottom.
   */
  listMessages(
    channelId: string,
    params: { before?: string; beforeId?: string; limit?: number } = {},
  ) {
    return this.apiClient.get(`channels/${channelId}/messages`, params)
  }

  sendMessage(channelId: string, data: ISendChannelMessageParams) {
    return this.apiClient.post(`channels/${channelId}/messages`, data)
  }

  // --- Scheduled messages (only ever the caller's own) ---

  scheduleMessage(channelId: string, data: IScheduleChannelMessageParams) {
    return this.apiClient.post(`channels/${channelId}/scheduled-messages`, data)
  }

  scheduleFirstMessage(data: IScheduleFirstMessageParams) {
    return this.apiClient.post('channels/scheduled-messages', data)
  }

  /** A file for a scheduled first message, held for its sender until the conversation exists. */
  uploadScheduledAttachment(file: File) {
    const formData = new FormData()
    formData.append('files[file]', file, file.name)
    return this.apiClient.post('channels/scheduled-attachments', formData)
  }

  listScheduledMessages() {
    return this.apiClient.get('channels/scheduled-messages', {})
  }

  updateScheduledMessage(scheduledMessageId: string, data: IUpdateScheduledChannelMessageParams) {
    return this.apiClient.put(`channels/scheduled-messages/${scheduledMessageId}`, data)
  }

  cancelScheduledMessage(scheduledMessageId: string) {
    return this.apiClient.delete(`channels/scheduled-messages/${scheduledMessageId}`)
  }

  sendScheduledMessageNow(scheduledMessageId: string) {
    return this.apiClient.post(`channels/scheduled-messages/${scheduledMessageId}/send-now`)
  }

  /**
   * Start an ad-hoc Zoom meeting for this conversation. The join card is posted
   * server-side, so it arrives over the same socket any other message does —
   * there is nothing to insert here.
   */
  startZoomMeeting(channelId: string) {
    return this.apiClient.post(`channels/${channelId}/zoom-meeting`)
  }

  editMessage(messageId: string, data: { contentState?: RawDraftContentState; content?: string }) {
    return this.apiClient.put(`channels/messages/${messageId}`, data)
  }

  deleteMessage(messageId: string) {
    return this.apiClient.delete(`channels/messages/${messageId}`)
  }

  /** One-way: there is no restore, so the card never comes back for that link. */
  dismissLinkPreview(messageId: string) {
    return this.apiClient.delete(`channels/messages/${messageId}/link-preview`)
  }

  /** Creates the task a conversation's prompt offered; answers with the anchor message. */
  acceptTaskSuggestion(suggestionId: string) {
    return this.apiClient.post(`channels/task-suggestions/${suggestionId}/accept`)
  }

  /** Dismisses the prompt for the whole conversation; answers with the anchor message. */
  dismissTaskSuggestion(suggestionId: string) {
    return this.apiClient.post(`channels/task-suggestions/${suggestionId}/dismiss`)
  }

  /**
   * The timeline window around one message, for arriving at a channel from a
   * search hit, pin or notification rather than reading it from the tail.
   */
  getMessageContext(messageId: string, params: { limit?: number } = {}) {
    return this.apiClient.get(`channels/messages/${messageId}/context`, params)
  }

  /** Participant sets the caller holds named conversations with — the sidebar groups. */
  listConversationGroups() {
    return this.apiClient.get('channels/conversation-groups', {})
  }

  /** The conversations inside one group. Paged: a group can hold hundreds. */
  listConversations(params: {
    dmKey?: string
    automationId?: string
    parentChannelId?: string
    search?: string
    limit?: number
    offset?: number
  }) {
    return this.apiClient.get('channels/conversations', params)
  }

  /**
   * The sidebar's Conversations section: everything recent across every
   * grouping, unread first. The only place unread is surfaced.
   */
  listRecentConversations(
    params: {
      search?: string
      limit?: number
      filter?: ThreadsFilter
      recentCount?: number
    } = {},
  ) {
    return this.apiClient.get('channels/recent-conversations', params)
  }

  /**
   * Pin or unpin anything the sidebar lists — a conversation, a channel or a
   * person. One endpoint because a pin is the same act whatever it points at.
   */
  setPin(target: { targetType: AimPinTargetType; targetId: string; isPinned: boolean }) {
    return this.apiClient.post('channels/pins', target)
  }

  /**
   * Put a conversation away until someone posts in it again. Per-viewer, and
   * distinct from archiving a channel.
   */
  archiveConversation(channelId: string, isArchived: boolean) {
    return this.apiClient.post(`channels/${channelId}/archive-conversation`, { isArchived })
  }

  /**
   * Write the shared notepad on a homepage. `scopeId` is a user id for a person
   * note — the server resolves it to the pair, so a caller cannot write to a
   * pair they are not in.
   */
  saveAimNote(
    scopeType: AimNoteScopeType,
    scopeId: string,
    content: string,
    contentState?: RawDraftContentState | null,
  ) {
    return this.apiClient.post('channels/notes', { scopeType, scopeId, content, contentState })
  }

  /** Everyone on the team — the People directory, not just people you have messaged. */
  listPeopleDirectory(params: { search?: string } = {}) {
    return this.apiClient.get('channels/people', params)
  }

  /** Start a new conversation inside a channel — the `+` on a channel row. */
  createChannelConversation(channelId: string, name?: string) {
    return this.apiClient.post(`channels/${channelId}/conversations`, name ? { name } : {})
  }

  // --- Homepages ---

  /** Conversations, tasks, pins and files for a channel, in one call. */
  getChannelHome(channelId: string, conversationLimit?: number, search?: string) {
    return this.apiClient.get(`channels/${channelId}/home`, { conversationLimit, search })
  }

  /** Arc's homepage — its conversations and the automations running on a schedule. */
  getArcHome(conversationLimit?: number, search?: string) {
    return this.apiClient.get('channels/arc/home', { conversationLimit, search })
  }

  /** The same blocks for a person, relative to the caller. */
  getPersonHome(userId: string, conversationLimit?: number, search?: string) {
    return this.apiClient.get(`channels/people/${userId}/home`, { conversationLimit, search })
  }

  /** Pick the model Arc runs on in this conversation. Null clears to the default. */
  setArcModel(channelId: string, model: string | null) {
    return this.apiClient.put(`channels/${channelId}/arc-model`, { model })
  }

  /** Arc automations the caller has scheduled runs for — the Scheduled runs section. */
  listScheduledRunGroups() {
    return this.apiClient.get('channels/scheduled-run-groups', {})
  }

  /** Omit messageId to mark everything currently in the channel as read. */
  markRead(channelId: string, messageId?: string) {
    return this.apiClient.post(`channels/${channelId}/read`, messageId ? { messageId } : {})
  }

  /** A `keepalive` fetch, which outlives the page: for marking read as the app is hidden or closed. */
  markReadOnPageLeave(channelId: string, messageId: string) {
    return this.apiClient.beacon(`channels/${channelId}/read`, { messageId })
  }

  /**
   * Rewinds the read cursor so this message, and everything after it, is unread.
   * With no `messageId` the whole conversation is: the newest root becomes the
   * target, so the badge comes back reading one — which is what a sidebar row can
   * ask for, holding `lastMessageAt` and no id.
   */
  markUnread(channelId: string, messageId?: string) {
    return this.apiClient.post(`channels/${channelId}/unread`, messageId ? { messageId } : {})
  }

  pinMessage(messageId: string, isPinned: boolean) {
    return this.apiClient.post(`channels/messages/${messageId}/pin`, { isPinned })
  }

  search(params: ISearchMessagesParams) {
    return this.apiClient.get('channels/search', params)
  }

  /** Conversations by name — threads, groups, and DMs by person. No query lists them all. */
  searchConversations(params: ISearchConversationsParams) {
    return this.apiClient.get('channels/search/conversations', params)
  }

  // --- Files ---

  /**
   * Two-step upload, matching the Atlas chat composer: the file goes up on its
   * own and the returned metadata is staged until the user hits send, so a slow
   * upload never blocks typing and a failed one never eats the typed body.
   */
  uploadAttachment(channelId: string, file: File) {
    const formData = new FormData()
    // `files[...]` is the key shape multer's `.any()` handler expects, matching
    // the Atlas chat upload — the route reads req.files[0].
    formData.append('files[file]', file, file.name)
    return this.apiClient.post(`channels/${channelId}/attachments`, formData)
  }

  /** Arc's reasoning trace for one message — see channels/GetArcProgress. */
  arcProgress(messageId: string) {
    return this.apiClient.get(`channels/messages/${messageId}/arc-progress`)
  }

  /** Every Arc run still in flight in a channel — see channels/ListArcRuns. */
  arcRuns(channelId: string) {
    return this.apiClient.get(`channels/${channelId}/arc-runs`)
  }

  /**
   * Stop Arc mid-run in a conversation. Keyed on the session, narrowed to one turn when
   * known — two mentions can be running in the same session (see channels/CancelArcRun).
   * Several turns at once are stopped with one notice in the conversation.
   */
  cancelArcRun(sessionId: string, turns?: string | string[] | null) {
    const body = Array.isArray(turns) ? { turnIds: turns } : turns ? { turnId: turns } : {}
    return this.apiClient.post(
      `channels/arc-sessions/${sessionId}/cancel`,
      body,
    ) as Promise<ICancelArcRunResult>
  }

  /**
   * The session's live plan. Session-scoped rather than part of `arcProgress`,
   * which is fetched once per Arc message — see channels/GetArcPlan.
   */
  arcPlan(sessionId: string) {
    return this.apiClient.get(`channels/arc-sessions/${sessionId}/plan`)
  }

  getAttachmentLink(attachmentId: string) {
    return this.apiClient.get(`channels/attachments/${attachmentId}/link`)
  }

  /** A short-lived URL the player streams a video from — see channels/GetAttachmentVideoUrl. */
  getAttachmentVideoUrl(attachmentId: string) {
    return this.apiClient.get(`channels/attachments/${attachmentId}/video-url`)
  }

  /** One request: the recording is stored and posted, with any typed body, as one message. */
  sendVoiceMemo(channelId: string, data: ISendVoiceMemoParams) {
    const formData = new FormData()
    formData.append('files[file]', data.file, data.file.name)
    formData.append('durationMs', String(Math.round(data.durationMs)))
    if (data.replyToMessageId) formData.append('replyToMessageId', data.replyToMessageId)
    if (data.contentState) formData.append('contentState', JSON.stringify(data.contentState))
    if (data.pageUrl) formData.append('pageUrl', data.pageUrl)
    return this.apiClient.post(`channels/${channelId}/voice-memos`, formData)
  }

  /**
   * Open a live voice call with Arc in this DM — see services/arcVoice/StartArcVoice.
   * `huddleId`: Arc is being brought into that huddle from this browser, not called alone.
   * `rejoinCallId`: the earlier call row this call picks up.
   */
  startArcVoice(
    channelId: string,
    pageUrl: string,
    device: ArcVoiceDevice,
    { huddleId, rejoinCallId }: { huddleId?: string; rejoinCallId?: string | null } = {},
  ) {
    return this.apiClient.post(`channels/${channelId}/arc-voice`, {
      pageUrl,
      device,
      ...(huddleId ? { huddleId } : {}),
      ...(rejoinCallId ? { rejoinCallId } : {}),
    }) as Promise<IArcVoiceSession>
  }

  // --- Huddles ---

  getHuddle(channelId: string) {
    return this.apiClient.get(`channels/${channelId}/huddle`) as Promise<IHuddle | null>
  }

  /** Start the conversation's huddle, or join the one already going. */
  startHuddle(channelId: string) {
    return this.apiClient.post(`channels/${channelId}/huddle`) as Promise<IHuddleJoin>
  }

  joinHuddle(channelId: string, huddleId: string) {
    return this.apiClient.post(
      `channels/${channelId}/huddles/${huddleId}/join`,
    ) as Promise<IHuddleJoin>
  }

  /** The heartbeat, carrying the mic state. `isSeated` false: this tab is no longer in the room. */
  updateHuddleSeat(channelId: string, huddleId: string, peerId: string, isMuted: boolean) {
    return this.apiClient.post(`channels/${channelId}/huddles/${huddleId}/seat`, {
      peerId,
      isMuted,
    }) as Promise<{ huddle: IHuddle | null; isSeated: boolean }>
  }

  /** A beacon when the page is unloading, so the seat is given up even as the tab closes. */
  leaveHuddle(channelId: string, huddleId: string, peerId: string, isUnloading = false) {
    const url = `channels/${channelId}/huddles/${huddleId}/leave`
    return isUnloading
      ? this.apiClient.beacon(url, { peerId })
      : this.apiClient.post(url, { peerId })
  }

  setHuddleArc(channelId: string, huddleId: string, peerId: string, isArcIn: boolean) {
    return this.apiClient.post(`channels/${channelId}/huddles/${huddleId}/arc`, {
      peerId,
      isArcIn,
    }) as Promise<IHuddle | null>
  }

  /** Whether Arc, hosted from this tab, has something worth saying to the room unasked. */
  judgeHuddleArcInsight(
    channelId: string,
    huddleId: string,
    peerId: string,
    lines: string[],
    arcSaid: string[],
  ) {
    return this.apiClient.post(`channels/${channelId}/huddles/${huddleId}/arc/insight`, {
      peerId,
      lines,
      arcSaid,
    }) as Promise<{ instruction: string | null }>
  }

  /** This browser was talking when a line was said to Arc: the request may run as its user. */
  confirmHuddleSpeaker(channelId: string, huddleId: string, checkId: string, peerId: string) {
    return this.apiClient.post(
      `channels/${channelId}/huddles/${huddleId}/speaker-checks/${checkId}`,
      { peerId },
    ) as Promise<{ isConfirmed: boolean }>
  }

  inviteToHuddle(channelId: string, huddleId: string, peerId: string, userIds: string[]) {
    return this.apiClient.post(`channels/${channelId}/huddles/${huddleId}/invite`, {
      peerId,
      userIds,
    }) as Promise<{ invitedUserIds: string[] }>
  }

  /** A stretch of this browser's own voice, for the huddle transcript. */
  addHuddleAudio(channelId: string, huddleId: string, peerId: string, chunk: IHuddleAudioChunk) {
    const formData = new FormData()
    formData.append('files[file]', chunk.file, chunk.file.name)
    formData.append('peerId', peerId)
    formData.append('startedAt', chunk.startedAt)
    return this.apiClient.post(`channels/${channelId}/huddles/${huddleId}/audio`, formData)
  }

  addHuddleArcLine(
    channelId: string,
    huddleId: string,
    peerId: string,
    text: string,
    spokenAt: string,
  ) {
    return this.apiClient.post(`channels/${channelId}/huddles/${huddleId}/arc/lines`, {
      peerId,
      text,
      spokenAt,
    })
  }

  /** Make one of a huddle summary's tasks a real task; made once, whoever clicks first. */
  createHuddleTask(channelId: string, huddleId: string, index: number) {
    return this.apiClient.post(
      `channels/${channelId}/huddles/${huddleId}/summary/tasks/${index}`,
      {},
    ) as Promise<{ taskId: string }>
  }

  /** Huddles in a group (and its threads), or ones a person was in. */
  listHuddleHistory(scope: { channelId: string } | { userId: string }) {
    return this.apiClient.get('huddles/history', scope) as Promise<IHuddleHistoryItem[]>
  }

  getHuddleTranscript(channelId: string, huddleId: string) {
    return this.apiClient.get(
      `channels/${channelId}/huddles/${huddleId}/transcript`,
    ) as Promise<IHuddleTranscript>
  }

  /** The page the caller just moved to, in words, for the call to know about. */
  describeArcVoicePage(channelId: string, pageUrl: string) {
    return this.apiClient.get(`channels/${channelId}/arc-voice/page`, { pageUrl }) as Promise<{
      pageContext: string | null
    }>
  }

  /**
   * The voice model's hand-off: post the spoken request to the DM and start Arc on it.
   * `speaker`: in a huddle, who said it; it runs as them once their own browser confirms.
   */
  askArcByVoice(
    channelId: string,
    request: string,
    pageUrl: string,
    callRecordId: string | null,
    device: ArcVoiceDevice,
    speaker?: { userId: string; lineMs: number; ageMs: number },
  ) {
    return this.apiClient.post(`channels/${channelId}/arc-voice/ask`, {
      request,
      pageUrl,
      device,
      ...(callRecordId ? { callRecordId } : {}),
      ...(speaker
        ? {
            onBehalfOfUserId: speaker.userId,
            speakerWindowMs: Math.round(speaker.lineMs),
            speakerAgeMs: Math.round(speaker.ageMs),
          }
        : {}),
    }) as Promise<IArcVoiceAnswer>
  }

  /** A new conversation with Arc to call it in, for a call started from nowhere in particular. */
  openArcVoiceConversation() {
    return this.apiClient.post('arc-voice/conversations') as Promise<{ channelId: string }>
  }

  /** Long-poll a spoken request until Arc settles or moves on to a new step. */
  getArcVoiceAnswer(channelId: string, chatHistoryId: string, knownProgress: string | null) {
    return this.apiClient.get(`channels/${channelId}/arc-voice/answers/${chatHistoryId}`, {
      ...(knownProgress ? { knownProgress } : {}),
    }) as Promise<IArcVoiceAnswer>
  }

  /** Start a call's row in the DM, as soon as the call connects; a rejoin ends the row it picks up. */
  recordArcVoiceCall(
    channelId: string,
    record: {
      startedAt: string
      durationMs: number
      transcript: IArcVoiceCallLine[]
      rejoinCallId?: string
    },
  ) {
    return this.apiClient.post(`channels/${channelId}/arc-voice/calls`, record) as Promise<{
      id: string
    }>
  }

  /**
   * Bring a call's row up to date, resolving to the row as it now stands. A beacon
   * when the page is unloading, so it still arrives, and nobody is left to read it.
   */
  async checkpointArcVoiceCall(
    channelId: string,
    recordId: string,
    checkpoint: IArcVoiceCallCheckpoint,
    isUnloading: boolean,
  ): Promise<IChannelMessage | null> {
    const url = `channels/${channelId}/arc-voice/calls/${recordId}`
    if (!isUnloading) return this.apiClient.post(url, checkpoint) as Promise<IChannelMessage>
    await this.apiClient.beacon(url, checkpoint)
    return null
  }

  retryVoiceMemoTranscription(attachmentId: string) {
    return this.apiClient.post(`channels/attachments/${attachmentId}/transcription`)
  }

  // --- Reactions ---

  addReaction(messageId: string, emoji: string) {
    return this.apiClient.post(`channels/messages/${messageId}/reactions`, { emoji })
  }

  removeReaction(messageId: string, emoji: string) {
    return this.apiClient.delete(`channels/messages/${messageId}/reactions`, { emoji })
  }
}
